import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Building2 } from 'lucide-react';
import type { SsoProviderId } from '@welynk/types';
import { SsoApiService } from '@/lib/services/account-api';

const LABELS: Record<SsoProviderId, string> = {
  GOOGLE: 'Google',
  MICROSOFT: 'Microsoft',
};

function ProviderMark({ provider }: { provider: SsoProviderId }) {
  if (provider === 'GOOGLE') {
    return (
      <svg width="16" height="16" viewBox="0 0 48 48" aria-hidden="true">
        <path
          fill="#FFC107"
          d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z"
        />
        <path
          fill="#FF3D00"
          d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"
        />
        <path
          fill="#4CAF50"
          d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-7.9l-6.5 5C9.5 39.6 16.2 44 24 44z"
        />
        <path
          fill="#1976D2"
          d="M43.6 20.5H42V20H24v8h11.3c-.8 2.3-2.2 4.2-4.1 5.6l6.2 5.2C37 39.2 44 34 44 24c0-1.3-.1-2.4-.4-3.5z"
        />
      </svg>
    );
  }
  return (
    <svg width="16" height="16" viewBox="0 0 23 23" aria-hidden="true">
      <path fill="#f35325" d="M1 1h10v10H1z" />
      <path fill="#81bc06" d="M12 1h10v10H12z" />
      <path fill="#05a6f0" d="M1 12h10v10H1z" />
      <path fill="#ffba08" d="M12 12h10v10H12z" />
    </svg>
  );
}

/**
 * "Continue with Google / Microsoft" plus, when the login link names a
 * college (`/login?college=<slug>`), a primary "Sign in with <college>" button
 * that restricts the account picker to the college's domain.
 */
export const SsoButtons: React.FC<{ returnTo?: string; collegeSlug?: string | null }> = ({
  returnTo,
  collegeSlug,
}) => {
  const { data } = useQuery({
    queryKey: ['sso-providers'],
    queryFn: () => SsoApiService.providers(),
    staleTime: 10 * 60_000,
    retry: false,
  });
  const { data: college } = useQuery({
    queryKey: ['sso-college', collegeSlug],
    queryFn: () => SsoApiService.college(collegeSlug!),
    enabled: !!collegeSlug,
    retry: false,
  });

  const providers = data?.providers ?? [];
  if (providers.length === 0 && !college) return null;

  const go = (provider: SsoProviderId, org?: string) => {
    window.location.assign(SsoApiService.startUrl(provider, { org, returnTo }));
  };

  return (
    <>
      <div className="auth-divider">
        <div className="l" />
        <span>or continue with</span>
        <div className="l" />
      </div>
      {college && (
        <button
          type="button"
          className="btn-auth-primary auth-cta"
          onClick={() => go(college.provider, collegeSlug!)}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 8,
            marginBottom: 10,
          }}
        >
          <Building2 size={16} aria-hidden="true" />
          Sign in with your {college.organizationName} account
        </button>
      )}
      {providers.length > 0 && (
        <div className="social-row">
          {providers.map((p) => (
            <button key={p} type="button" className="social-btn" onClick={() => go(p)}>
              <ProviderMark provider={p} />
              {LABELS[p]}
            </button>
          ))}
        </div>
      )}
    </>
  );
};
